import React, { useEffect, useState } from 'react';
import { User, Application, ApplicationStatus, Interview } from '../types';
import { api } from '../services/api';
import { CheckCircle2, Clock, Calendar, ExternalLink, UserCheck, Video, Sparkles } from 'lucide-react';

interface ApplicantDashboardProps {
  user: User;
  onLaunchVideoRoom: (interview: Interview) => void;
}

const STEPS: { status: ApplicationStatus; label: string }[] = [
  { status: 'submitted', label: 'Submitted' },
  { status: 'under_review', label: 'Under Review' },
  { status: 'interviewing', label: 'Interviewing' },
  { status: 'offered', label: 'Offer Extended' },
];

export const ApplicantDashboard: React.FC<ApplicantDashboardProps> = ({ user, onLaunchVideoRoom }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMyData = async () => {
    setLoading(true);
    try {
      const [appsRes, intsRes] = await Promise.all([
        api.getMyApplications(),
        api.getMyInterviews().catch(() => ({ interviews: [] })),
      ]);
      setApplications(appsRes.applications || []);
      setInterviews(intsRes.interviews || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyData();
  }, [user]);

  const getStepIndex = (status: ApplicationStatus) => {
    switch (status) {
      case 'submitted': return 0;
      case 'under_review': return 1;
      case 'interviewing': return 2;
      case 'offered': return 3;
      case 'rejected': return -1;
      default: return 0;
    }
  };

  return (
    <div style={{ padding: '40px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800 }}>
            Candidate Dashboard
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '4px' }}>
            Track real-time status, upcoming interviews, and feedback for your SansCraft applications.
          </p>
        </div>

        <button className="btn-secondary" onClick={fetchMyData}>
          Refresh Applications
        </button>
      </div>

      {/* Upcoming Scheduled Video Interviews Banner */}
      {interviews.length > 0 && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(0, 242, 255, 0.1), rgba(13, 23, 44, 0.9))',
            border: '1px solid var(--color-aqua-border)',
            padding: '24px',
            borderRadius: 'var(--radius-lg)',
            marginBottom: '32px',
            boxShadow: 'var(--shadow-glow-aqua)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-aqua)', fontWeight: 700, fontSize: '0.85rem' }}>
            <Sparkles size={16} /> Live Video Interview Scheduled
          </div>
          <h3 style={{ fontSize: '1.4rem', fontWeight: 800, marginTop: '4px' }}>
            Upcoming Interview: {interviews[0].job_title}
          </h3>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginTop: '16px' }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-aqua)', fontWeight: 600 }}>
                <Calendar size={16} /> {new Date(interviews[0].scheduled_at).toLocaleString()}
              </span>
              <span>Interviewer: {interviews[0].interviewer_email}</span>
            </div>

            <button className="btn-primary" onClick={() => onLaunchVideoRoom(interviews[0])}>
              <Video size={18} /> Join Live Video Interview Room
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>
          Loading your submitted applications...
        </div>
      ) : error ? (
        <div style={{ color: 'var(--accent-rose)', padding: '20px', background: 'rgba(244, 63, 94, 0.1)', borderRadius: 'var(--radius-md)' }}>
          {error}
        </div>
      ) : applications.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '60px 20px',
            background: 'var(--color-navy-card)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-beige-border)',
          }}
        >
          <UserCheck size={48} color="var(--color-aqua)" style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: 700 }}>No Applications Submitted Yet</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '6px', maxWidth: '480px', margin: '6px auto 20px auto' }}>
            Explore open team roles in Development, Moderation, and Infrastructure to submit your first application.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {applications.map((app) => {
            const currentStepIdx = getStepIndex(app.status);
            const isRejected = app.status === 'rejected';

            return (
              <div
                key={app.id}
                style={{
                  background: 'var(--color-navy-card)',
                  border: '1px solid var(--color-beige-border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '28px',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <span className="status-pill" style={{ marginBottom: '8px' }}>
                      {app.job_department || 'General'}
                    </span>
                    <h3 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-main)' }}>
                      {app.job_title || 'Position Application'}
                    </h3>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Calendar size={14} /> Submitted {new Date(app.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className={`status-pill ${app.status}`}>
                      {app.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Tracking Progress Timeline */}
                <div className="timeline">
                  {STEPS.map((step, idx) => {
                    let stateClass = '';
                    if (isRejected) {
                      stateClass = idx === 0 ? 'completed' : '';
                    } else if (idx < currentStepIdx) {
                      stateClass = 'completed';
                    } else if (idx === currentStepIdx) {
                      stateClass = 'active';
                    }

                    return (
                      <div key={step.status} className={`timeline-step ${stateClass} ${isRejected && idx === currentStepIdx ? 'rejected' : ''}`}>
                        <div className="step-icon">
                          {stateClass === 'completed' ? (
                            <CheckCircle2 size={18} />
                          ) : (
                            idx + 1
                          )}
                        </div>
                        <span className="step-label">{step.label}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Reviewer Notes */}
                {app.reviewer_notes && (
                  <div
                    style={{
                      marginTop: '20px',
                      background: 'rgba(0, 242, 255, 0.05)',
                      border: '1px solid var(--color-aqua-border)',
                      padding: '16px',
                      borderRadius: 'var(--radius-md)',
                    }}
                  >
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--color-aqua)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Hiring Team Update & Notes
                    </span>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-main)', marginTop: '4px' }}>
                      {app.reviewer_notes}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
